import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { listingsApi, getFileUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { getCrownVicImage, handleImageError } from '../lib/images';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Car, Plus, DollarSign, MapPin, Calendar, Edit, Trash2, Eye } from 'lucide-react';

export default function MyListingsPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const response = await listingsApi.getMine();
        setListings(response.data);
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, []);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return;
    
    try {
      await listingsApi.delete(id);
      setListings(listings.filter(l => l.id !== id));
    } catch (error) {
      alert('Failed to delete listing');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 tag">Loading your listings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-between">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Dashboard</p>
              <h1 className="font-heading font-bold text-4xl text-white mb-2">My Listings</h1>
              <p className="text-gray-400">Manage your Crown Vic listings</p>
            </div>
            <Link to="/marketplace">
              <Button className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="new-listing-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Listing
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {listings.length > 0 ? (
          <div className="space-y-4">
            {listings.map((listing) => (
              <Card key={listing.id} className="bg-[#141414] border-[#262626] p-4" data-testid={`my-listing-${listing.id}`}>
                <div className="flex gap-4">
                  <div className="w-32 h-24 flex-shrink-0 rounded overflow-hidden bg-[#262626]">
                    <img
                      src={listing.images?.[0] ? getFileUrl(listing.images[0]) : getCrownVicImage(listing.id)}
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-lg text-white mb-1 truncate">{listing.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400 mb-2">
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4 text-[#FF3B30]" />
                        {formatPrice(listing.price)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {listing.year}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {listing.location}
                      </span>
                    </div>
                    <span className={`tag px-2 py-0.5 rounded ${listing.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                      {listing.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Link to={`/marketplace/${listing.id}`}>
                      <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" data-testid={`view-listing-${listing.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(listing.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-listing-${listing.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Car className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h3 className="font-heading font-bold text-xl text-white mb-2">No Listings Yet</h3>
            <p className="text-gray-400 mb-6">You haven't listed any cars for sale.</p>
            <Link to="/marketplace">
              <Button className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="create-first-listing-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Listing
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
